import { useState, useEffect, useCallback } from 'react';
import { Pane, TextInputField, Button, toaster, Checkbox, SelectField, Heading, Text, Paragraph, ArchiveIcon } from 'evergreen-ui';
import { getUserDetails, updateUserDetails, updateCollaborator, deleteUserAttachment } from '../../services/collaboratorServices';
import FileUploaderMultipleUploadDemo from '../../commons/FileUploaderMultipleUploadDemo';
import { useAuth } from '../../services/authContext';
import { isValidIBAN } from 'ibantools';

const UserDetailsForm = ({ userId, onSaved, baseUser }) => {
    const { user } = useAuth();
    const effectiveIsDirector = user?.role === 'direttore' || !!user?.is_advanced;
    const [formData, setFormData] = useState({});
    const [initialData, setInitialData] = useState({});
    
    // Base User state
    const [baseUserData, setBaseUserData] = useState({
        nome: '',
        cognome: '',
        role: '',
        email: '',
        password: ''
    });
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ibanError, setIbanError] = useState('');

    const fetchDetails = useCallback(async () => {
        try {
            const data = await getUserDetails(userId);
            if (data && !data.error) {
                setFormData(data);
                setInitialData(data);
            }
        } catch (err) {
            console.error(err);
        }
    }, [userId]);

    useEffect(() => {
        if (baseUser) {
            setBaseUserData({
                id: baseUser.id || '',
                nome: baseUser.nome || '',
                cognome: baseUser.cognome || '',
                role: baseUser.role || '',
                email: baseUser.email || '',
                password: ''
            });
        }
        fetchDetails();
    }, [userId, baseUser, fetchDetails]);

    const handleDeleteAttachment = async (attachmentId) => {
        if (!window.confirm("Sei sicuro di voler eliminare questo allegato?")) return;
        
        try {
            const resp = await deleteUserAttachment(attachmentId);
            if (resp && resp.success) {
                toaster.success('Allegato eliminato con successo.');
                fetchDetails();
            } else {
                toaster.danger('Errore durante l\'eliminazione: ' + (resp?.error || 'Sconosciuto'));
            }
        } catch (err) {
            console.error(err);
            toaster.danger('Errore di rete durante l\'eliminazione dell\'allegato.');
        }
    };

    const handleTextChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'iban') {
            const cleanValue = value.replace(/\s+/g, '').toUpperCase();
            if (cleanValue && !isValidIBAN(cleanValue)) {
                setIbanError('L\'IBAN inserito non sembra essere valido');
            } else {
                setIbanError('');
            }
        }
    };

    const handleFilesChange = (acceptedFiles) => {
        setAttachments(acceptedFiles);
    };

    const handleBaseUserChange = (field, value) => {
        setBaseUserData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (formData.iban && ibanError) {
            toaster.danger('Correggi l\'IBAN prima di salvare.');
            setLoading(false);
            return;
        }

        try {
            // Se è il direttore, aggiorniamo anche i dati base (users.php)
            if (effectiveIsDirector && baseUser) {
                const updatePayload = {
                    id: baseUser.id,
                    email: baseUserData.email,
                    nome: baseUserData.nome,
                    cognome: baseUserData.cognome,
                    role: baseUserData.role
                };
                if (baseUserData.password && baseUserData.password.trim() !== '') {
                    updatePayload.password = baseUserData.password;
                }

                const baseJson = await updateCollaborator(updatePayload);
                if (baseJson.error) {
                    throw new Error(baseJson.error);
                }
            }

            const data = new FormData();
            data.append('user_id', userId);

            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined && typeof formData[key] !== 'object') {
                    data.append(key, formData[key]);
                }
            });

            if (attachments && attachments.length > 0) {
                attachments.forEach((file, index) => {
                    data.append(`attachments[${index}]`, file);
                });
            }

            const resp = await updateUserDetails(data);
            if (resp && resp.success) {
                toaster.success('Dati aggiornati con successo');
                if (onSaved) onSaved();
            } else {
                toaster.danger('Errore dettagli: ' + (resp?.error || 'Sconosciuto'));
            }
        } catch (err) {
            toaster.danger('Errore durante il salvataggio: ' + err.message);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Pane paddingTop={16}>
            <Heading size={500} marginBottom={16}>Compila i tuoi dati aggiuntivi facoltativi</Heading>
            <form onSubmit={handleSubmit}>
                <Pane display="flex" flexDirection="column" gap={16}>
                    {effectiveIsDirector && baseUser && (
                        <Pane background="tint1" padding={16} borderRadius={4} border="muted" marginBottom={16}>
                            <Heading size={400} marginBottom={12}>Dati base utente (modificabili solo dal direttore)</Heading>
                            <Pane display="flex" gap={16} flexWrap="wrap">
                                <TextInputField
                                    label="Email"
                                    value={baseUserData.email}
                                    onChange={e => handleBaseUserChange('email', e.target.value)}
                                    flex={1} minWidth={150}
                                />
                                <TextInputField
                                    label="Nome"
                                    value={baseUserData.nome} 
                                    onChange={e => handleBaseUserChange('nome', e.target.value)} 
                                    flex={1} minWidth={150} 
                                />
                                <TextInputField 
                                    label="Cognome" 
                                    value={baseUserData.cognome} 
                                    onChange={e => handleBaseUserChange('cognome', e.target.value)} 
                                    flex={1} minWidth={150} 
                                />
                                <SelectField 
                                    label="Ruolo" 
                                    value={baseUserData.role} 
                                    onChange={e => handleBaseUserChange('role', e.target.value)} 
                                    flex={1} minWidth={150}
                                >
                                    <option value="educatore">Educatore</option>
                                    <option value="operatore">Operatore</option>
                                </SelectField>
                                <TextInputField 
                                    label="Nuova password" 
                                    type="password"
                                    placeholder="Lascia vuoto per non modificare"
                                    value={baseUserData.password} 
                                    onChange={e => handleBaseUserChange('password', e.target.value)} 
                                    flex={1} minWidth={150} 
                                />
                            </Pane>
                        </Pane>
                    )}
                    <Heading size={400}>Dati anagrafici</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Codice fiscale" value={formData.cf || ''} onChange={e => handleTextChange('cf', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Data di nascita" type="date" value={formData.data_nascita || ''} onChange={e => handleTextChange('data_nascita', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Luogo di nascita" value={formData.luogo_nascita || ''} onChange={e => handleTextChange('luogo_nascita', e.target.value)} flex={1} minWidth={200} />
                    </Pane>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Indirizzo di residenza" value={formData.indirizzo_residenza || ''} onChange={e => handleTextChange('indirizzo_residenza', e.target.value)} flex={2} minWidth={250} />
                        <TextInputField label="CAP" value={formData.cap || ''} onChange={e => handleTextChange('cap', e.target.value)} flex={1} minWidth={100} />
                        <TextInputField label="Comune" value={formData.comune || ''} onChange={e => handleTextChange('comune', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Provincia" value={formData.provincia || ''} onChange={e => handleTextChange('provincia', e.target.value)} flex={1} minWidth={80} />
                    </Pane>

                    <Heading size={400}>Dati di contatto</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Recapito telefonico personale" value={formData.telefono_personale || ''} onChange={e => handleTextChange('telefono_personale', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Recapito telefonico alternativo" value={formData.telefono_alternativo || ''} onChange={e => handleTextChange('telefono_alternativo', e.target.value)} flex={1} minWidth={200} />
                    </Pane>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Email personale" type="email" value={formData.email_personale || ''} onChange={e => handleTextChange('email_personale', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Indirizzo PEC" type="email" value={formData.pec || ''} onChange={e => handleTextChange('pec', e.target.value)} flex={1} minWidth={200} />
                    </Pane>
                    <Checkbox label="Autorizzo l'utilizzo dell'indirizzo mail fornito per l'invio di comunicazioni aziendali" 
                        disabled={initialData.consenso_comunicazioni === 1 || initialData.consenso_comunicazioni === true} 
                        checked={formData.consenso_comunicazioni === 1 || formData.consenso_comunicazioni === true} 
                        onChange={e => handleTextChange('consenso_comunicazioni', e.target.checked ? 1 : 0)} 
                    />
                    
                    <Heading size={400}>Inquadramento contrattuale</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Data di assunzione" type="date" value={formData.data_assunzione || ''} onChange={e => handleTextChange('data_assunzione', e.target.value)} flex={1} minWidth={200} />
                        {(formData.tipo_contratto === 'tempo_determinato' || formData.tipo_contratto === 'apprendistato') && (
                            <TextInputField label="Termine rapporto" type="date" value={formData.termine_rapporto || ''} onChange={e => handleTextChange('termine_rapporto', e.target.value)} flex={1} minWidth={200} />
                        )}
                        <SelectField label="Tipologia di contratto" value={formData.tipo_contratto || ''} onChange={e => handleTextChange('tipo_contratto', e.target.value)} flex={1} minWidth={200}>
                            <option value="">Seleziona...</option>
                            <option value="tempo_indeterminato">Tempo indeterminato</option>
                            <option value="tempo_determinato">Tempo determinato</option>
                            <option value="apprendistato">Apprendistato</option>
                            <option value="sostituzione">Sostituzione</option>
                            <option value="altro">Altro (specificare)</option>
                        </SelectField>
                        {formData.tipo_contratto === 'altro' && (
                            <TextInputField label="Specifica altro contratto" value={formData.note_tipo_contratto || ''} onChange={e => handleTextChange('note_tipo_contratto', e.target.value)} flex={1} minWidth={200} />
                        )}
                    </Pane>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <SelectField label="Orario di lavoro" value={formData.orario_lavoro || ''} onChange={e => handleTextChange('orario_lavoro', e.target.value)} flex={1} minWidth={200}>
                            <option value="">Seleziona...</option>
                            <option value="full_time">Full Time</option>
                            <option value="part_time">Part Time</option>
                        </SelectField>
                        {formData.orario_lavoro === 'part_time' && (
                            <TextInputField label="Ore settimanali part-time" type="number" value={formData.ore_part_time || ''} onChange={e => handleTextChange('ore_part_time', e.target.value)} flex={1} minWidth={200} />
                        )}
                        <TextInputField label="CCNL applicato" value="CCNL UNEBA" disabled flex={1} minWidth={200} />
                    </Pane>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField
                            label="Ore mensili previste"
                            type="number"
                            value={formData.ore_mensili_previste ? parseFloat(formData.ore_mensili_previste).toString() : ''}
                            disabled={!effectiveIsDirector}
                            onChange={e => handleTextChange('ore_mensili_previste', parseFloat(e.target.value) || 0)}
                            flex={1}
                            minWidth={200}
                        />
                        <TextInputField
                            label="Ore giornaliere previste"
                            type="number"
                            value={formData.ore_giornaliere_previste ? parseFloat(formData.ore_giornaliere_previste).toString() : ''}
                            disabled={!effectiveIsDirector}
                            onChange={e => handleTextChange('ore_giornaliere_previste', parseFloat(e.target.value) || 0)}
                            flex={1}
                            minWidth={200}
                        />
                    </Pane>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Livello di inquadramento" value={formData.livello_inquadramento || ''} onChange={e => handleTextChange('livello_inquadramento', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Mansione o profilo professionale" value={formData.mansione || ''} onChange={e => handleTextChange('mansione', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Sede di lavoro assegnata" value={formData.sede_lavoro || ''} onChange={e => handleTextChange('sede_lavoro', e.target.value)} flex={1} minWidth={200} />
                    </Pane>

                    <Heading size={400}>Documentazione amministrativa</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField 
                            label="IBAN per l'accredito delle retribuzioni" 
                            placeholder="IT..."
                            value={formData.iban || ''} 
                            onChange={e => handleTextChange('iban', e.target.value)} 
                            flex={2} 
                            minWidth={250} 
                            isInvalid={ibanError !== ''}
                            validationMessage={ibanError || null}
                        />
                        <TextInputField 
                            label="Intestatario/Cointestatari" 
                            placeholder="Nome e Cognome"
                            value={formData.iban_intestatario || ''} 
                            onChange={e => handleTextChange('iban_intestatario', e.target.value)} 
                            flex={1} 
                            minWidth={200} 
                        />
                        <TextInputField 
                            label="Banca d'appoggio" 
                            placeholder="es. Intesa Sanpaolo"
                            value={formData.iban_banca || ''} 
                            onChange={e => handleTextChange('iban_banca', e.target.value)} 
                            flex={1} 
                            minWidth={200} 
                        />
                    </Pane>
                    
                    <Pane display="flex" flexDirection="column" gap={12} background="tint1" padding={16} borderRadius={8}>
                        <Paragraph size={300} color="muted" marginBottom={8}>
                            Carica qui tutti i tuoi documenti. Sono richiesti: Copia documento d'identità, Codice fiscale, Documentazione sanitaria, Casellario giudiziale e ogni altro documento utile (saranno uniti a quelli già salvati).
                        </Paragraph>
                        
                        <FileUploaderMultipleUploadDemo onFilesChange={handleFilesChange} />
                        
                        {(formData.attachments && formData.attachments.length > 0) && (
                            <Pane marginTop={16}>
                                <Heading size={300} marginBottom={8}>File già caricati:</Heading>
                                <ul style={{ listStyleType: 'disc', paddingLeft: 20, margin: 0, fontSize: '0.9rem', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                    {formData.attachments.map(att => (
                                        <li key={att.id} style={{ marginBottom: 4 }}>
                                            <Pane display="flex" alignItems="center" gap={8} flexWrap="wrap">
                                                <a href={att.file_path} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', overflowWrap: 'anywhere', wordBreak: 'break-all', maxWidth: '100%' }}>{att.file_name}</a>
                                                {user?.role === 'direttore' && (
                                                    <Text
                                                        color="danger"
                                                        cursor="pointer"
                                                        textDecoration="underline"
                                                        onClick={() => handleDeleteAttachment(att.id)}
                                                        size={300}
                                                    >
                                                        Elimina
                                                    </Text>
                                                )}
                                            </Pane>
                                        </li>
                                    ))}
                                </ul>
                            </Pane>
                        )}
                    </Pane>

                    <Heading size={400}>Contatto in caso di emergenza</Heading>
                    <Pane display="flex" gap={16} flexWrap="wrap">
                        <TextInputField label="Nome e cognome" value={formData.emergenza_nome || ''} onChange={e => handleTextChange('emergenza_nome', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Relazione" value={formData.emergenza_relazione || ''} onChange={e => handleTextChange('emergenza_relazione', e.target.value)} flex={1} minWidth={200} />
                        <TextInputField label="Telefono" value={formData.emergenza_telefono || ''} onChange={e => handleTextChange('emergenza_telefono', e.target.value)} flex={1} minWidth={200} />
                    </Pane>

                    <Heading size={400}>Autocertificazione assenza carichi penali</Heading>
                    <Pane background="tint1" padding={16} borderRadius={8} border="muted">
                        <Paragraph size={300} marginBottom={12}>
                            <strong>Autocertificazione assenza condanne penali</strong> (art. 46, comma 1, D.P.R. 28 dicembre 2000 n. 445).<br/>
                            Ai sensi dell'art. 76 D.P.R. 445/2000, sotto la propria responsabilità penale, il/la sottoscritto/a dichiara:
                        </Paragraph>
                        <Checkbox
                            label="di non aver subito condanne penali comunque rientranti fra quelle previste per i reati di cui agli articoli 600-bis, 600-ter, 600-quater, 600-quinquies e 609-undecies del codice penale, ovvero l'irrogazione di sanzioni interdittive all'esercizio di attività che comportino contatti diretti e regolari con minori"
                            disabled={initialData.cp_nessuna_condanna === 1 || initialData.cp_nessuna_condanna === true}
                            checked={formData.cp_nessuna_condanna === 1 || formData.cp_nessuna_condanna === true}
                            onChange={e => handleTextChange('cp_nessuna_condanna', e.target.checked ? 1 : 0)}
                        />
                        <Checkbox
                            label="di non aver carichi pendenti e comunque non essere a conoscenza di essere sottoposto/a a procedimenti penali"
                            disabled={initialData.cp_nessun_carico_pendente === 1 || initialData.cp_nessun_carico_pendente === true}
                            checked={formData.cp_nessun_carico_pendente === 1 || formData.cp_nessun_carico_pendente === true}
                            onChange={e => handleTextChange('cp_nessun_carico_pendente', e.target.checked ? 1 : 0)}
                        />
                        <Checkbox
                            label="che è residente nell'ambito del circondario della Procura della Repubblica presso il Tribunale di competenza del comune di residenza e che, pertanto, il casellario giudiziale e le certificazioni dei carichi pendenti possono essere richiesti presso tale Autorità"
                            disabled={initialData.cp_residente_circondario === 1 || initialData.cp_residente_circondario === true}
                            checked={formData.cp_residente_circondario === 1 || formData.cp_residente_circondario === true}
                            onChange={e => handleTextChange('cp_residente_circondario', e.target.checked ? 1 : 0)}
                        />
                    </Pane>

                    <Heading size={400}>Dichiarazioni e privacy</Heading>
                    <Pane background="yellowTint" padding={16} borderRadius={8}>
                        <Paragraph size={300} marginBottom={16}>
                            <strong>Informativa Privacy (art.13 GDPR):</strong><br/>
                            Ai sensi del regolamento UE 2016/679 (GDPR), informiamo che i dati raccolti tramite il presente modulo sono trattati da Abrakadabra Servizi alla Persona S.r.l. per finalità gestionali, amministrative e connesse all'esecuzione del rapporto di lavoro. Il conferimento dei dati è obbligatorio ai fini della corretta gestione del rapporto contrattuale. I dati non saranno diffusi e aranno conservati per il periodo necessario al rispetto degli obblighi di legge. Il dipendente può esercitare i diritti previsti dagli artt. 15-22 GDPR contattando il titolare del trattamento previsto presso la sede legale.
                        </Paragraph>
                        <Checkbox
                            label="Ho letto e compreso l'Informativa Privacy (vedi al fondo del sito)"
                            disabled={initialData.letto_privacy_policy === 1 || initialData.letto_privacy_policy === true}
                            checked={formData.letto_privacy_policy === 1 || formData.letto_privacy_policy === true}
                            onChange={e => handleTextChange('letto_privacy_policy', e.target.checked ? 1 : 0)}
                        />
                        <Checkbox
                            label="Dichiaro che i dati sopra riportati sono veritieri e mi impegno a comunicare tempestivamente eventuali variazioni di recapito, residenza, PEC o altri dati utili alla gestione amministrativa del rapporto di lavoro."
                            disabled={initialData.dichiara_veritieri === 1 || initialData.dichiara_veritieri === true}
                            checked={formData.dichiara_veritieri === 1 || formData.dichiara_veritieri === true}
                            onChange={e => handleTextChange('dichiara_veritieri', e.target.checked ? 1 : 0)}
                        />
                        {formData.dichiara_veritieri ? (
                            <TextInputField
                                label="Data della firma/dichiarazione"
                                type="date"
                                disabled={initialData.dichiara_veritieri === 1 || initialData.dichiara_veritieri === true}
                                value={formData.data_dichiarazione || ''}
                                onChange={e => handleTextChange('data_dichiarazione', e.target.value)}
                                maxWidth={250}
                                marginTop={8}
                            />
                        ): null}
                    </Pane>

                    <Button appearance="primary" intent="success" size="large" type="submit" isLoading={loading} marginTop={16}>
                        Salva i dati <ArchiveIcon marginLeft={8} />
                    </Button>
                </Pane>
            </form>
        </Pane>
    );
};

export default UserDetailsForm;
